import { useMemo } from "react";
import { IconShieldLock } from "@tabler/icons-react";
import { useLocation, useNavigate } from "react-router-dom";
import { NavButton } from "./NavButton";
import type { NavLinkProps } from "../utils/consts";
import { AdminScope } from "./auth/AdminScope";
import { useAuth } from "../hooks/useAuth";

export interface NavbarProps {
  primary: NavLinkProps[];
  footer: NavLinkProps[];
  onNavigate?: () => void;
}

function resolveActiveLinkId(
  pathname: string,
  visible: NavLinkProps[],
): number | null {
  const path = pathname.toLowerCase();
  const invoiceDetail = path === "/invoice" || path.startsWith("/invoice/");
  const customerDetail =
    path === "/customers" || path.startsWith("/customers/");
  if (invoiceDetail || customerDetail) {
    const receivablesNav = visible.find((l) => l.url === "/receivables");
    if (receivablesNav) return receivablesNav.id;
  }
  const exact = visible.find((l) => l.url === pathname);
  if (exact) return exact.id;
  const sorted = [...visible].sort((a, b) => b.prefix.length - a.prefix.length);
  for (const link of sorted) {
    const pl = link.prefix.toLowerCase();
    if (path.startsWith(pl)) {
      if (
        path.length === pl.length ||
        path[pl.length] === "/" ||
        path[pl.length] === undefined
      ) {
        return link.id;
      }
    }
  }
  return null;
}

export const Navbar = ({ primary, footer, onNavigate }: NavbarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { scopes } = useAuth();

  const visiblePrimary = useMemo(
    () => primary.filter((link) => scopes?.includes(link.scope)),
    [primary, scopes],
  );
  const visibleFooter = useMemo(
    () => footer.filter((link) => scopes?.includes(link.scope)),
    [footer, scopes],
  );

  const allVisible = useMemo(
    () => [...visiblePrimary, ...visibleFooter],
    [visiblePrimary, visibleFooter],
  );

  const activeId = useMemo(
    () => resolveActiveLinkId(location.pathname, allVisible),
    [allVisible, location.pathname],
  );

  const goTo = (url: string) => {
    navigate(url);
    onNavigate?.();
  };

  const mapLink = (link: NavLinkProps) => (
    <NavButton
      key={link.id}
      id={link.id}
      Icon={link.icon}
      label={link.label}
      selected={link.id === activeId}
      onClick={() => goTo(link.url)}
    />
  );

  const adminLink = (
    <AdminScope>
      <NavButton
        key={999}
        id={999}
        Icon={IconShieldLock}
        label="Admin"
        selected={"/admin" === location.pathname}
        onClick={() => goTo("/admin")}
      />
    </AdminScope>
  );

  return (
    <nav className="flex h-full min-h-0 w-20 flex-col items-center overflow-y-auto backdrop-blur-lg py-4">
      <div className="flex w-full flex-col gap-3">
        {visiblePrimary.map(mapLink)}
      </div>
      <div className="mt-auto flex w-full flex-col gap-3 pt-3">
        {visibleFooter.map(mapLink)}
        {adminLink}
      </div>
    </nav>
  );
};
